import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUniversalSearch } from '../../hooks/useUniversalSearch';
import { UniversalSearchResult } from '../../types/search.types';
import './UniversalSearch.css';

interface UniversalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const UniversalSearch: React.FC<UniversalSearchProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const { query, setQuery, data, loading, error, reset } = useUniversalSearch(isOpen);

  const flatResults = useMemo(() => data?.results ?? [], [data]);

  useEffect(() => {
    if (isOpen) {
      const timer = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    setActiveIndex(0);
  }, [flatResults]);

  const selectResult = useCallback(
    (result: UniversalSearchResult) => {
      onClose();
      reset();
      navigate(result.url);
    },
    [navigate, onClose, reset]
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }

    if (!flatResults.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % flatResults.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => (prev - 1 + flatResults.length) % flatResults.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const result = flatResults[activeIndex];
      if (result) selectResult(result);
    }
  };

  useEffect(() => {
    const activeEl = listRef.current?.querySelector('[data-active="true"]');
    activeEl?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!isOpen) return null;

  const showEmpty = query.trim().length >= 2 && !loading && !error && flatResults.length === 0;
  const showHint = query.trim().length < 2;

  return (
    <div className="universal-search-overlay" onClick={onClose} role="presentation">
      <div
        className="universal-search-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('search.title')}
      >
        <div className="universal-search-input-row">
          <span className="material-icons universal-search-input-icon" aria-hidden="true">
            search
          </span>
          <input
            ref={inputRef}
            type="search"
            className="universal-search-input"
            placeholder={t('search.placeholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label={t('search.ariaLabel')}
            autoComplete="off"
            spellCheck={false}
          />
          {loading ? (
            <span className="universal-search-spinner" aria-hidden="true" />
          ) : (
            <kbd className="universal-search-kbd">esc</kbd>
          )}
        </div>

        <div className="universal-search-body" ref={listRef}>
          {showHint && (
            <div className="universal-search-hint">
              <p>{t('search.hint')}</p>
              <p className="universal-search-hint-shortcut">
                <kbd>⌘</kbd> <kbd>K</kbd> {t('search.or')} <kbd>Ctrl</kbd> <kbd>K</kbd>{' '}
                {t('search.shortcutOpen')}
              </p>
            </div>
          )}

          {error && <div className="universal-search-empty">{error}</div>}

          {showEmpty && (
            <div className="universal-search-empty">
              {t('search.noResults', { query: query.trim() })}
            </div>
          )}

          {data?.groups?.map((group) => (
            <section key={group.type} className="universal-search-group">
              <h3 className="universal-search-group-label">
                {t(`search.types.${group.type}`, { defaultValue: group.label })}
              </h3>
              <ul className="universal-search-results">
                {group.items.map((result) => {
                  const globalIndex = flatResults.findIndex(
                    (r) => r.type === result.type && r.id === result.id
                  );
                  const isActive = globalIndex === activeIndex;

                  return (
                    <li key={`${result.type}-${result.id}`}>
                      <button
                        type="button"
                        className="universal-search-result"
                        data-active={isActive ? 'true' : 'false'}
                        onClick={() => selectResult(result)}
                        onMouseEnter={() => setActiveIndex(globalIndex)}
                      >
                        <span className="universal-search-result-icon material-icons" aria-hidden="true">
                          {result.icon}
                        </span>
                        <span className="universal-search-result-text">
                          <span className="universal-search-result-title">{result.title}</span>
                          {result.subtitle ? (
                            <span className="universal-search-result-subtitle">{result.subtitle}</span>
                          ) : null}
                        </span>
                        <span className="universal-search-result-type">
                          {t(`search.types.${result.type}`, { defaultValue: result.typeLabel })}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>

        <div className="universal-search-footer">
          <span>
            <kbd>↑</kbd> <kbd>↓</kbd> {t('search.footerNavigate')}
          </span>
          <span>
            <kbd>↵</kbd> {t('search.footerOpen')}
          </span>
          <span>
            <kbd>esc</kbd> {t('search.footerClose')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default UniversalSearch;
